import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import './EmployeesView.css';

const EmployeesView = () => {
  const navigate = useNavigate();
  const { users, exportEmployees, attendance, updateAttendance } = useData();
  const { currentUser, changeEmployeePassword } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const employees = users.filter(u => u.role !== 'owner');

  // Get unique departments
  const departments = [...new Set(employees.map(emp => emp.department).filter(Boolean))];

  // Filter employees based on search and filters
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || employee.role === filterRole;
    const matchesDepartment = filterDepartment === 'all' || employee.department === filterDepartment;

    return matchesSearch && matchesRole && matchesDepartment;
  });

  const getRoleColor = (role) => {
    const colors = {
      project_manager: '#3b82f6',
      hr: '#10b981', 
      employee: '#6b7280'
    };
    return colors[role] || '#6b7280';
  };

  const getRoleLabel = (role) => {
    const labels = {
      project_manager: 'Project Manager',
      hr: 'HR Manager',
      employee: 'Employee'
    };
    return labels[role] || 'Employee';
  };

  const getDepartmentColor = (department) => {
    const colors = {
      'Technology': '#8b5cf6',
      'Human Resources': '#10b981',
      'Development': '#3b82f6',
      'Marketing': '#f59e0b',
      'Sales': '#ef4444',
      'Finance': '#06b6d4'
    };
    return colors[department] || '#6b7280';
  };

  const getStatusColor = (isActive) => {
    return isActive ? '#10b981' : '#ef4444';
  };

  // Handle View Profile
  const handleViewProfile = (employee) => {
    setSelectedEmployee(employee);
    // For now, show alert - you can implement proper profile modal
    alert(`Viewing profile for ${employee.name}\n\nEmail: ${employee.email}\nRole: ${getRoleLabel(employee.role)}\nDepartment: ${employee.department}\nJoined: ${new Date(employee.joinDate).toLocaleDateString()}`);
  };

  // Handle Send Message
  const handleSendMessage = (employee) => {
    navigate('/messages', { state: { recipientId: employee.id, recipientName: employee.name } });
  };

  // Handle Password Change (Edit functionality)
  const handleEditEmployee = (employee) => {
    if (currentUser?.role !== 'owner') {
      alert('Only owners can edit employee accounts');
      return;
    }
    setSelectedEmployee(employee);
    setShowPasswordModal(true);
  };

  // Handle Password Reset
  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    try {
      await changeEmployeePassword(selectedEmployee.id, newPassword);
      alert(`Password updated successfully for ${selectedEmployee.name}`);
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedEmployee(null);
    } catch (error) {
      alert('Failed to update password. Please try again.');
      console.error('Password update error:', error);
    }
  };

  // Handle Attendance Calendar
  const handleViewAttendance = (employee) => {
    setSelectedEmployee(employee);
    setCurrentMonth(new Date());
    setShowAttendanceModal(true);
  };

  // Get employee attendance for specific month
  const getEmployeeAttendance = (employeeId, month) => {
    const year = month.getFullYear();
    const monthNum = month.getMonth();

    return attendance.filter(record => {
      const recordDate = new Date(record.date);
      return record.userId === employeeId && 
             recordDate.getFullYear() === year && 
             recordDate.getMonth() === monthNum;
    });
  };

  // Update attendance status for a specific date
  const handleAttendanceUpdate = async (date, status) => {
    if (!selectedEmployee) return;

    const dateStr = date.toISOString().split('T')[0];
    const existingRecord = attendance.find(record => 
      record.userId === selectedEmployee.id && record.date === dateStr
    );

    try {
      if (existingRecord) {
        // Update existing record
        await updateAttendance(existingRecord.id, { status });
      } else {
        // Create new record
        const newRecord = {
          id: Date.now().toString(),
          userId: selectedEmployee.id,
          userName: selectedEmployee.name,
          date: dateStr,
          status: status,
          checkIn: status === 'present' ? '09:00' : null,
          checkOut: status === 'present' ? '17:00' : null,
          hoursWorked: status === 'present' ? 8 : 0
        };
        await updateAttendance(null, newRecord);
      }

      alert(`Attendance updated for ${selectedEmployee.name} on ${date.toLocaleDateString()}`);
    } catch (error) {
      alert('Failed to update attendance');
      console.error('Attendance update error:', error);
    }
  };

  // Download employee attendance Excel
  const handleDownloadAttendance = (employee) => {
    const employeeAttendance = attendance.filter(record => record.userId === employee.id);

    if (employeeAttendance.length === 0) {
      alert(`No attendance records found for ${employee.name}`);
      return;
    }

    // Create CSV content
    const csvContent = [
      ['Employee Name', 'Date', 'Check In', 'Check Out', 'Hours Worked', 'Status'],
      ...employeeAttendance.map(record => [
        record.userName,
        record.date,
        record.checkIn || '-',
        record.checkOut || '-',
        record.hoursWorked || 0,
        record.status || 'present'
      ])
    ].map(row => row.join(',')).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${employee.name}_attendance_report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Generate calendar days
  const generateCalendarDays = (month) => {
    const year = month.getFullYear();
    const monthNum = month.getMonth();
    const firstDay = new Date(year, monthNum, 1);
    const lastDay = new Date(year, monthNum + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= lastDay || days.length % 7 !== 0) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
      if (days.length > 42) break; // Safety limit
    }

    return days;
  };

  const getAttendanceStatus = (date) => {
    if (!selectedEmployee) return null;
    const dateStr = date.toISOString().split('T')[0];
    const record = attendance.find(r => r.userId === selectedEmployee.id && r.date === dateStr);
    return record?.status || null;
  };

  return (
    <div className="emp-view-container">
      {/* Header */}
      <div className="emp-dashboard-header">
        <div className="emp-header-content">
          <h2 className="emp-dashboard-title">Employee Management</h2>
          <p className="emp-dashboard-subtitle">Manage and view all company employees</p>
        </div>
        <button onClick={exportEmployees} className="emp-export-btn">
          <i className="fas fa-download"></i>
          Export Employees
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="emp-controls-container">
        <div className="emp-search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="emp-search-input"
          />
        </div>

        <div className="emp-filter-controls">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="emp-filter-select"
          >
            <option value="all">All Roles</option>
            <option value="project_manager">Project Manager</option>
            <option value="hr">HR Manager</option>
            <option value="employee">Employee</option>
          </select>

          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="emp-filter-select"
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Employee Stats */}
      <div className="emp-stats-container">
        <div className="emp-stat-card">
          <span className="emp-stat-number">{employees.length}</span>
          <span className="emp-stat-label">Total Employees</span>
        </div>
        <div className="emp-stat-card">
          <span className="emp-stat-number">{employees.filter(e => e.status === 'active').length}</span>
          <span className="emp-stat-label">Active</span>
        </div>
        <div className="emp-stat-card">
          <span className="emp-stat-number">{departments.length}</span>
          <span className="emp-stat-label">Departments</span>
        </div>
        <div className="emp-stat-card">
          <span className="emp-stat-number">{filteredEmployees.length}</span>
          <span className="emp-stat-label">Showing</span>
        </div>
      </div>

      {/* Employees Grid */}
      <div className="emp-grid-container">
        {filteredEmployees.map(employee => (
          <div key={employee.id} className="emp-card">
            <div className="emp-card-header">
              <div className="emp-avatar">
                {employee.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="emp-status-indicator">
                <div 
                  className="emp-status-dot"
                  style={{ backgroundColor: getStatusColor(employee.status === 'active' || employee.status == null) }}
                ></div>
              </div>
            </div>

            <div className="emp-details-section">
              <h3 className="emp-name">{employee.name}</h3>
              <p className="emp-email">{employee.email}</p>

              <div className="emp-badges-container">
                <span 
                  className="emp-role-badge"
                  style={{ backgroundColor: getRoleColor(employee.role) }}
                >
                  {getRoleLabel(employee.role)}
                </span>
                {employee.department && (
                  <span 
                    className="emp-dept-badge"
                    style={{ backgroundColor: getDepartmentColor(employee.department) }}
                  >
                    {employee.department}
                  </span>
                )}
              </div>

              <div className="emp-meta-container">
                <div className="emp-meta-item">
                  <i className="fas fa-calendar-alt"></i>
                  <span>Joined {new Date(employee.joinDate).toLocaleDateString()}</span>
                </div>
                <div className="emp-meta-item">
                  <i className="fas fa-id-badge"></i>
                  <span>ID: {employee.id.substring(0, 8)}</span>
                </div>
                {employee.phone && (
                  <div className="emp-meta-item">
                    <i className="fas fa-phone"></i>
                    <span>{employee.phone}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="emp-actions-container">
              <button 
                className="emp-action-btn emp-view-btn"
                onClick={() => handleViewProfile(employee)}
                title="View Profile"
              >
                <i className="fas fa-eye"></i>
                View
              </button>

              {(currentUser?.role === 'owner' || currentUser?.role === 'hr') && (
                <button 
                  className="emp-action-btn emp-calendar-btn"
                  onClick={() => handleViewAttendance(employee)}
                  title="View Attendance Calendar"
                >
                  <i className="fas fa-calendar-check"></i>
                  Calendar
                </button>
              )}

              {currentUser?.role === 'owner' && (
                <>
                  <button 
                    className="emp-action-btn emp-download-btn"
                    onClick={() => handleDownloadAttendance(employee)}
                    title="Download Attendance Report"
                  >
                    <i className="fas fa-download"></i>
                    Report
                  </button>

                  <button 
                    className="emp-action-btn emp-edit-btn"
                    onClick={() => handleEditEmployee(employee)}
                    title="Change Password"
                  >
                    <i className="fas fa-key"></i>
                    Edit
                  </button>
                </>
              )}

              <button 
                className="emp-action-btn emp-message-btn"
                onClick={() => handleSendMessage(employee)}
                title="Send Message"
              >
                <i className="fas fa-envelope"></i>
                Message
              </button>
            </div>
          </div>
        ))}

        {filteredEmployees.length === 0 && (
          <div className="emp-no-results">
            <div className="emp-no-results-icon">
              <i className="fas fa-users"></i>
            </div>
            <h3>No Employees Found</h3>
            <p>
              {searchTerm || filterRole !== 'all' || filterDepartment !== 'all'
                ? 'No employees match your current filters.'
                : 'No employee records available.'
              }
            </p>
            {(searchTerm || filterRole !== 'all' || filterDepartment !== 'all') && (
              <button 
                className="emp-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilterRole('all');
                  setFilterDepartment('all');
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="emp-modal-overlay">
          <div className="emp-modal">
            <div className="emp-modal-header">
              <h3>Change Password for {selectedEmployee?.name}</h3>
              <button 
                className="emp-modal-close"
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setSelectedEmployee(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="emp-modal-body">
              <div className="emp-form-group">
                <label className="emp-form-label">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="emp-form-input"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
            </div>
            <div className="emp-modal-actions">
              <button 
                className="emp-modal-btn emp-cancel-btn"
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setSelectedEmployee(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="emp-modal-btn emp-save-btn"
                onClick={handlePasswordReset}
              >
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Calendar Modal */}
      {showAttendanceModal && selectedEmployee && (
        <div className="emp-modal-overlay">
          <div className="emp-modal emp-calendar-modal">
            <div className="emp-modal-header">
              <h3>Attendance Calendar - {selectedEmployee.name}</h3>
              <button 
                className="emp-modal-close"
                onClick={() => {
                  setShowAttendanceModal(false);
                  setSelectedEmployee(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="emp-modal-body">
              <div className="emp-calendar-nav">
                <button 
                  className="emp-nav-btn"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span className="emp-month-year">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button 
                  className="emp-nav-btn"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>

              <div className="emp-calendar">
                <div className="emp-calendar-header">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="emp-calendar-day-header">{day}</div>
                  ))}
                </div>
                <div className="emp-calendar-body">
                  {generateCalendarDays(currentMonth).map((date, index) => {
                    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                    const status = getAttendanceStatus(date);
                    const isToday = date.toDateString() === new Date().toDateString();

                    return (
                      <div 
                        key={index} 
                        className={`emp-calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${status ? 'has-status' : ''}`}
                      >
                        <span className="emp-day-number">{date.getDate()}</span>
                        {status && (
                          <div className={`emp-status-indicator ${status}`}></div>
                        )}
                        {isCurrentMonth && (currentUser?.role === 'owner' || currentUser?.role === 'hr') && (
                          <div className="emp-day-actions">
                            <button 
                              className="emp-status-btn present"
                              onClick={() => handleAttendanceUpdate(date, 'present')}
                              title="Mark Present"
                            >
                              P
                            </button>
                            <button 
                              className="emp-status-btn absent"
                              onClick={() => handleAttendanceUpdate(date, 'absent')}
                              title="Mark Absent"
                            >
                              A
                            </button>
                            <button 
                              className="emp-status-btn leave"
                              onClick={() => handleAttendanceUpdate(date, 'leave')}
                              title="Mark Leave"
                            >
                              L
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="emp-calendar-legend">
                <div className="emp-legend-item">
                  <div className="emp-legend-color present"></div>
                  <span>Present</span>
                </div>
                <div className="emp-legend-item">
                  <div className="emp-legend-color absent"></div>
                  <span>Absent</span>
                </div>
                <div className="emp-legend-item">
                  <div className="emp-legend-color leave"></div>
                  <span>Leave</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesView;